import { useParams, Link } from "react-router-dom";
import { ArrowUpRight } from "lucide-react";
import { useSeo } from "@/hooks/useSeo";
import { Reveal } from "@/components/motion/Reveal";
import Breadcrumbs from "@/components/Breadcrumbs";
import { insights } from "@/data/content";
import { articleSchema, monthToIso } from "@/data/seo";
import NotFound from "@/pages/NotFound";

function Body({ block }) {
  if (block.type === "h2")
    return (
      <h2 className="mt-10 font-display text-2xl font-extrabold leading-tight text-ink sm:mt-12 md:mt-14 md:text-3xl">
        {block.text}
      </h2>
    );
  if (block.type === "quote")
    return (
      <blockquote className="my-12 border-l-2 border-accent pl-6 font-serif text-2xl italic leading-snug text-ink md:text-4xl">
        {block.text}
      </blockquote>
    );
  if (block.type === "list")
    return (
      <ul className="mt-6 space-y-3">
        {block.items.map((it) => (
          <li key={it} className="flex gap-4 text-lg leading-relaxed text-muted">
            <span className="mt-2 h-1.5 w-1.5 shrink-0 rounded-full bg-accent" />
            <span>{it}</span>
          </li>
        ))}
      </ul>
    );
  return <p className="mt-6 text-lg leading-relaxed text-muted">{block.text}</p>;
}

export default function InsightDetail() {
  const { slug } = useParams();
  const post = insights.find((p) => p.slug === slug);
  const iso = monthToIso(post?.date);
  useSeo({
    title: post ? post.title : "Insight",
    description: post ? post.dek || post.excerpt : "Insight",
    path: `/insights/${slug}`,
    image: post?.image,
    type: "article",
    pageType: "ItemPage",
    noindex: !post,
    breadcrumbs: post ? [{ name: "Insights", path: "/insights" }, { name: post.title, path: `/insights/${slug}` }] : undefined,
    article: post ? { published: iso, section: post.category } : undefined,
    schema: post ? [articleSchema(post)] : [],
  });
  if (!post) return <NotFound />;
  const others = insights.filter((p) => p.slug !== slug).slice(0, 3);

  return (
    <>
      <article data-testid="insight-detail">
        <section className="border-b border-line pt-24 sm:pt-28 md:pt-32 lg:pt-36">
          <div className="mx-auto max-w-[1000px] px-5 sm:px-8 md:px-10 lg:px-12 xl:px-16">
            <Breadcrumbs items={[{ name: "Insights", path: "/insights", testId: "back-to-insights" }, { name: post.category, path: `/insights/${slug}` }]} />
            <div className="pb-12">
              <p className="mb-5 flex items-center gap-3 text-xs font-bold uppercase tracking-[0.22em] text-accent">
                {post.category} <span className="h-px w-8 bg-line" /> {post.read}
              </p>
              <h1 className="font-display text-4xl font-extrabold leading-[0.98] tracking-tight text-ink md:text-6xl">
                {post.title}
              </h1>
              {post.dek && (
                <p className="mt-6 max-w-2xl font-serif text-xl italic leading-snug text-muted md:text-2xl">
                  {post.dek}
                </p>
              )}
              <div className="mt-8 flex items-center gap-3 text-sm text-muted">
                <span className="font-bold uppercase tracking-[0.14em] text-ink">{post.author}</span>
                <span className="h-px w-6 bg-line" />
                <time dateTime={iso}>{post.date}</time>
              </div>
            </div>
          </div>
        </section>

        {post.image && (
          <div className="mx-auto max-w-[1200px] px-5 sm:px-8 md:px-10 lg:px-12 xl:px-16">
            <Reveal className="my-12 aspect-[16/9] overflow-hidden bg-line md:my-16">
              <img src={post.image} alt={post.title} className="h-full w-full object-cover" />
            </Reveal>
          </div>
        )}

        <section className="mx-auto max-w-[1000px] px-5 pb-16 sm:pb-20 md:pb-24 lg:pb-28 sm:px-8 md:px-10 lg:px-12 xl:px-16">
          <div data-testid="insight-body" className="max-w-2xl">
            {post.body?.map((block, i) => (
              <Body key={i} block={block} />
            ))}
          </div>
        </section>
      </article>

      <section className="border-t border-line bg-surface">
        <div className="mx-auto max-w-[1600px] px-5 py-14 sm:py-16 md:py-20 lg:py-24 xl:py-28 sm:px-8 md:px-10 lg:px-12 xl:px-16">
          <div className="mb-8 flex items-end justify-between md:mb-10">
            <h2 className="font-display text-3xl font-extrabold text-ink md:text-5xl">More insights</h2>
            <Link
              to="/insights"
              className="group inline-flex items-center gap-2 text-sm font-bold uppercase tracking-[0.14em] text-ink"
            >
              View all <ArrowUpRight size={16} className="transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
            </Link>
          </div>
          <div className="grid gap-x-8 gap-y-8 sm:gap-y-10 md:grid-cols-3">
            {others.map((o) => (
              <Link
                key={o.slug}
                to={`/insights/${o.slug}`}
                data-testid={`related-insight-${o.slug}`}
                className="group block border-t border-line pt-6"
              >
                <p className="text-xs font-bold uppercase tracking-[0.14em] text-accent">{o.category}</p>
                <h3 className="mt-3 font-display text-2xl font-extrabold leading-tight text-ink transition-colors group-hover:text-accent">
                  {o.title}
                </h3>
                <p className="mt-3 text-sm leading-relaxed text-muted">{o.excerpt}</p>
              </Link>
            ))}
          </div>
        </div>
      </section>
    </>
  );
}
